import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Building2, 
  Users, 
  FileText, 
  Check, 
  ChevronRight,
  Sparkles,
  MessageSquare
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnboardingWizard = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState({
    company_created: false,
    first_client_added: false,
    first_invoice_created: false
  });

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/companies/onboarding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSteps(response.data.steps);
      setCurrentStep(response.data.current_step);
      
      if (response.data.completed) {
        onComplete?.();
      }
    } catch (error) {
      console.error("Onboarding status error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStepAction = (step) => {
    switch (step) {
      case 1:
        navigate("/dashboard/settings");
        break;
      case 2:
        navigate("/dashboard/clients");
        break;
      case 3:
        navigate("/dashboard/invoices/new");
        break;
      default:
        break;
    }
  };

  const handleSkip = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      await axios.post(`${API}/companies/onboarding/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSkip?.();
    } catch (error) {
      console.error("Skip onboarding error:", error);
      onSkip?.();
    }
  };

  const stepsConfig = [
    {
      id: 1,
      title: "Configura tu empresa",
      description: "Ingresa los datos fiscales de tu empresa para empezar a facturar",
      icon: Building2,
      completed: steps.company_created,
      action: "Configurar empresa"
    },
    {
      id: 2,
      title: "Agrega tu primer cliente",
      description: "Registra los datos de un cliente para emitirle facturas",
      icon: Users,
      completed: steps.first_client_added,
      action: "Agregar cliente"
    },
    {
      id: 3,
      title: "Crea tu primera factura",
      description: "Genera tu primer CFDI y envíalo por correo electrónico",
      icon: FileText,
      completed: steps.first_invoice_created,
      action: "Crear factura"
    }
  ];

  const completedSteps = stepsConfig.filter(s => s.completed).length;
  const progress = (completedSteps / stepsConfig.length) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }

  if (completedSteps === stepsConfig.length) {
    return null;
  }

  return (
    <Card className="cia-card overflow-hidden mb-6" data-testid="onboarding-wizard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#5B9BD5] to-[#4A89C4] p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold">¡Bienvenido a CIA Factura℗!</h2>
              <p className="text-white/80 text-sm">Completa estos pasos para empezar a facturar</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white hover:bg-white/10"
            onClick={handleSkip}
          >
            Omitir
          </Button>
        </div>
        
        {/* Progress */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span>{completedSteps} de {stepsConfig.length} completados</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-white/20" />
        </div>
      </div>

      {/* Steps */}
      <CardContent className="p-6">
        <div className="space-y-4">
          {stepsConfig.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.completed;
            const isLocked = step.id > currentStep && !isCompleted;

            return (
              <div
                key={step.id}
                className={`flex items-center gap-4 p-4 rounded-lg transition-all ${
                  isActive 
                    ? "bg-[#5B9BD5]/10 border-2 border-[#5B9BD5]" 
                    : isCompleted 
                      ? "bg-green-50 border border-green-200"
                      : "bg-slate-50 border border-slate-200"
                } ${isLocked ? "opacity-50" : ""}`}
              >
                {/* Step number/icon */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isCompleted 
                    ? "bg-green-500 text-white" 
                    : isActive 
                      ? "bg-[#5B9BD5] text-white"
                      : "bg-slate-200 text-slate-500"
                }`}>
                  {isCompleted ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <Icon className="w-6 h-6" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className={`font-semibold ${isCompleted ? "text-green-700" : "text-slate-900"}`}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>

                {/* Action */}
                {!isCompleted && !isLocked && (
                  <Button
                    onClick={() => handleStepAction(step.id)}
                    className={isActive ? "cia-btn-primary" : ""}
                    variant={isActive ? "default" : "outline"}
                    data-testid={`onboarding-step-${step.id}-btn`}
                  >
                    {step.action}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}

                {isCompleted && (
                  <span className="text-green-600 font-medium text-sm flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Completado
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* WhatsApp tip */}
        <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-green-800 font-medium text-sm">¿Sabías que puedes facturar por WhatsApp?</p>
              <p className="text-green-700 text-sm">
                Una vez configurada tu empresa, podrás enviar mensajes a nuestro asistente y crear facturas 
                desde cualquier lugar. ¡Prueba escribiendo "Hola" en el chat!
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingWizard;
